export interface StoryBlock {
  type: "p" | "h2" | "quote";
  text: string;
}

export interface LegendStory {
  id: string;
  title: string;
  category: "夜间传说" | "旧事档案" | "真相求证";
  tagline: string;
  meta: string;
  place: string;
  time: string;
  readTime: string;
  cover: string;
  excerpt: string;
  conclusion: string;
  verdict: "有据可考" | "有历史原型" | "证据不足存疑" | "基本可证为误传";
  blocks: StoryBlock[];
}

export const legendStories: LegendStory[] = [
  {
    id: "hongyi-xuejie",
    title: "红衣学姐的传闻，到底从哪里开始的？",
    category: "真相求证",
    tagline: "一条在几代女生宿舍楼里代代相传的传说",
    meta: "求证 · 女生宿舍楼",
    place: "某南方师范类院校",
    time: "上世纪九十年代至今",
    readTime: "6 分钟",
    cover: "https://readdy.ai/api/search-image?query=a%20moody%20dark%20academic%20building%20corridor%20at%20night%20with%20a%20single%20warm%20lamp%20glow%2C%20long%20shadow%20and%20red%20scarf%20detail%2C%20ink%20wash%20illustration%20style%2C%20grainy%20atmosphere%2C%20deep%20brown%20and%20amber%20tones&width=1200&height=760&seq=legend-xuejie-01&orientation=landscape",
    excerpt: "几乎每所老牌院校的女生宿舍都流传着「红衣学姐」的版本。我们追溯了这个传说最早被记录的年代与它可能的现实原型。",
    conclusion: "流传范围极广但不同学校版本雷同，未见任何一版存在可核实的当事人。最可能的来源是九十年代一部校园题材影视作品带来的集体记忆。",
    verdict: "基本可证为误传",
    blocks: [
      { type: "p", text: "「红衣学姐」大概是全中国分布最广的校园怪谈之一：据说宿舍楼某间空寝室住着一位穿红衣的学姐，深夜会敲门、会借东西，也有人说是每层楼熄灯后站在走廊尽头的影子。问题在于，几乎每所学校都有相似说法，而细节却高度雷同。" },
      { type: "h2", text: "最早可查的记录出现在哪里" },
      { type: "p", text: "我们检索了上世纪八十到九十年代的校园刊物与地方报纸，最早出现「红衣学姐」这一明确称呼的记录，集中在一九九七到二〇〇〇年之间。而在更早的文献里，类似传说要么没有颜色指向，要么被称作「白衣女」或「楼道里的影子」，说法并不统一。" },
      { type: "quote", text: "多个彼此相隔上千公里、年代相差十余年的学校，却流传着几乎一字不差的同一则细节——这本身就说明，它更像是一条被反复转述的「共享模板」。" },
      { type: "h2", text: "与九十年代流行文化的重合" },
      { type: "p", text: "值得注意的是，九十年代中后期内地引进了多部日本与港台校园恐怖片，「红色校服」「红衣学姐」成为银幕上的经典意象。同一时期各地校园论坛与广播站开始大量复述相关内容，时间线高度吻合。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "综合现有材料，我们未找到任何一版「红衣学姐」存在可核实、可联系的真实当事人或原始事件。它更接近一种「校园共享文化模板」：由影视与文字媒介催生，再被不同学校的学生各自本地化。因此我们将其评定为基本可证为误传，但它作为一代代人的共同记忆，本身仍值得被记录。" },
    ],
  },
  {
    id: "tushuguan-yedeng",
    title: "图书馆深夜亮灯，是值班老师还是别的什么？",
    category: "夜间传说",
    tagline: "闭馆后顶层总有一扇窗亮着，据说没人敢上去",
    meta: "求证 · 老校区图书馆",
    place: "某工科院校老校区",
    time: "二〇〇五年前后",
    readTime: "5 分钟",
    cover: "https://readdy.ai/api/search-image?query=a%20silhouette%20of%20an%20old%20library%20building%20at%20night%20with%20one%20warm%20lit%20window%20high%20up%2C%20fog%20and%20bare%20branches%2C%20dark%20ink%20wash%20editorial%20illustration%2C%20amber%20and%20deep%20brown%20palette&width=1200&height=760&seq=legend-lib-02&orientation=landscape",
    excerpt: "闭馆后顶层资料室那盏常亮的灯，成了几届学生的夜谈话题。我们找到了真正住在楼里、每晚开灯的人。",
    conclusion: "灯是住在顶楼值班室的老管理员为整理古籍文献而亮。他在此工作近三十年，习惯深夜安静地修补旧书。",
    verdict: "有据可考",
    blocks: [
      { type: "p", text: "在老校区流传最广的夜间传说之一，是关于图书馆顶层那盏灯。据说闭馆后整栋楼漆黑一片，唯独资料室方向的窗常年亮着，有人说是档案在自行翻阅，也有人说是某位早已离开的老师的灯一直没有熄灭。" },
      { type: "h2", text: "亮灯的人，其实一直住在楼里" },
      { type: "p", text: "求证过程中我们辗转联系到该校图书馆的多位在职与退休职工。答案出乎意料地平凡：顶层靠里有一间被隔出的值班室，住了近三十年的是负责古籍整理与修补的老管理员周师傅。他不习惯宿舍生活，又嗜好在深夜安静地修复虫蛀发脆的旧书。" },
      { type: "quote", text: "周师傅说，他最喜欢深夜翻旧书，因为「纸张被翻动的声音，在白天根本听不见」。灯亮了几十年，只是因为有个人在守护几千册更老的纸。" },
      { type: "h2", text: "传闻为何与「鬼火」挂钩" },
      { type: "p", text: "真正让传说走样的是视觉：值班室的暖光灯透过发黄的老式窗格，在远处看会有轻微闪烁，加上冬季室内热空气造成的光晕，很容易被夜归的学生解读成「忽明忽暗的不明光源」。距离与想象共同完成了二次加工。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "这则传说存在明确的人、时间与工作内容，属于有据可考。我们提醒所有夜归的同学：下次看到那扇亮着的窗，那大概是一位老管理员正在灯下，替一本更老的书续命。" },
    ],
  },
  {
    id: "kexue-lou-guandao",
    title: "科学楼地下室的管道声，为什么总在阴雨天出现",
    category: "旧事档案",
    tagline: "一座老科学楼，一段关于暖气与敲击声的旧事",
    meta: "旧事档案 · 科学楼",
    place: "某综合性大学理学院旧楼",
    time: "一九九〇年代建成",
    readTime: "7 分钟",
    cover: "https://readdy.ai/api/search-image?query=old%20science%20building%20basement%20corridor%20with%20aged%20pipes%20and%20steam%2C%20damp%20walls%2C%20dim%20single%20bulb%2C%20retro%20documentary%20photography%20mood%2C%20deep%20brown%20green%20amber%20tones&width=1200&height=760&seq=legend-science-03&orientation=landscape",
    excerpt: "「阴雨天就响」的规律性，反而成了解开这则旧事的钥匙。我们走访了当年的基建负责人。",
    conclusion: "声响来自老式供暖与排水管道在气压变化下的热胀冷缩与气锤效应，阴雨天水汽与气压变化最明显，因此「随天气出现」并非巧合。",
    verdict: "有历史原型",
    blocks: [
      { type: "p", text: "不少老科学楼都流传着地下室有异响的说法。传闻里那通常是敲击声、拖拽声或「金属碰金属」的闷响，而最蹊跷的一点是：很多亲历者都说它只在阴雨天或天气骤变时出现。这个「规律」恰恰给了我们求证方向。" },
      { type: "h2", text: "阴雨天与声响的物理关联" },
      { type: "p", text: "我们咨询了当年负责老楼管网的基建负责人与供暖老师傅。结论指向一套非常实在的机制：老楼供暖与排水系统采用老式铸铁管道，天气骤变时气压与湿度剧烈波动，管内蒸汽与积水状态改变，导致热胀冷缩和「水锤效应」——也就是管道内积水被气流推动时发出的沉闷敲击。" },
      { type: "quote", text: "老师傅的原话是：「不是楼里有什么，是这一整套铁管子，一到天气变脸就要'咳嗽'几声。」" },
      { type: "h2", text: "传闻如何被添上想象" },
      { type: "p", text: "在缺少供暖常识的年代，规律的敲击声被赋予了「某次事故留下的痕迹」等叙事。加上地下室光线昏暗、气味潮湿，脚步声与回声都会放大情绪。传闻在几届学生间流传后，逐渐脱离物理原貌。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "声响有明确的物理来源，且「阴雨天响」完全符合管道的物性规律。因此本则属于有历史原型——真实的旧楼与真实的管道，加上被浪漫化的想象。如今老楼已改用新式管道，传说也随之安静下来。" },
    ],
  },
  {
    id: "caochang-zhonglou",
    title: "操场边老钟楼的钟，真的会在凌晨自己响一声吗",
    category: "夜间传说",
    tagline: "一口早已停摆的钟，却在守门人口中从未停过",
    meta: "求证 · 操场钟楼",
    place: "某西北老校中学",
    time: "一九六〇年代至今",
    readTime: "5 分钟",
    cover: "https://readdy.ai/api/search-image?query=ancient%20school%20clock%20tower%20silhouette%20under%20a%20moon%20in%20gobi%20desert%20edge%20campus%2C%20single%20dim%20street%20lamp%2C%20very%20dark%20blue%20night%20sky%20kept%20desaturated%20to%20warm%20brown%2C%20editorial%20illustration&width=1200&height=760&seq=legend-clock-04&orientation=landscape",
    excerpt: "六十多年里，钟楼换过几任守钟人，但「凌晨会响一声」的说法始终没断过。我们找到了现任守门人。",
    conclusion: "钟确实会响，但响的是被遗留在塔顶检修时未取下的铁铃，热胀冷缩在凌晨气温最低时发出的脆响，与「报时」无关。",
    verdict: "有据可考",
    blocks: [
      { type: "p", text: "在西北一所老中学，操场边的钟楼是建校初期的地标。传闻说，虽然报时的钟早在八十年代就停摆，但每到凌晨气温最低的时候，塔顶仍会传出一下清脆的「当」声。学生中间流传着各种版本：有人说是钟舍不得停下，也有人说是当年出事的人回来了。" },
      { type: "h2", text: "守门人三十年的答案" },
      { type: "p", text: "现任守门人老赵在此工作超过三十年。他告诉我们，声音确实存在，他亲耳听过无数次。但答案并不神秘：塔顶检修口旁遗留着一枚未取下的铁铃，它并非报时钟，而是早年用作提示晨起的旧铃。西北昼夜温差大，凌晨气温降至最低时，铁铃的金属结构收缩，与锈蚀的支架摩擦碰撞，便发出那一下脆响。" },
      { type: "quote", text: "「它不是在报时，是在提醒修塔的人还没把梯子搬走。」老赵笑着补了一句，「我们早习惯了，就当它是楼上那枚铃在'睡醒'。」" },
      { type: "h2", text: "为什么只在凌晨" },
      { type: "p", text: "一日之中凌晨往往是气温最低点，金属收缩最剧烈，因此发声概率最高。白天日晒后金属膨胀、声音被环境噪音掩盖，几乎听不见。这个「只在凌晨响」的规律，正是金属物理而非神秘力量的体现。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "声源、机制、发生时间均得到现任与历任多位守塔人员的印证，属于有据可考。传闻里唯一保真的，其实是「凌晨会响一声」这个现象本身——只是声音的主人，是一枚被遗忘的旧铃。" },
    ],
  },
  {
    id: "laohoushan-muduo",
    title: "老校区后山那片林子，为什么总被人说「进去会迷路」",
    category: "旧事档案",
    tagline: "一片总让人「绕回原地」的林子，藏着的其实是地图没画出来的路",
    meta: "求证 · 后山林区",
    place: "某山林校区",
    time: "校区扩展时期",
    readTime: "6 分钟",
    cover: "https://readdy.ai/api/search-image?query=dense%20pine%20forest%20path%20with%20thick%20undergrowth%20on%20a%20hill%20behind%20a%20school%20at%20dusk%2C%20fog%20between%20trees%2C%20moody%20green%20brown%20monochrome%20leaning%20warm%2C%20mysterious%20editorial%20photography&width=1200&height=760&seq=legend-hill-05&orientation=landscape",
    excerpt: "关于后山迷路的传说版本很多，最离奇的说法是「走了很久又回到同一棵歪脖子树」。我们重走了那条路。",
    conclusion: "迷路体验是真实的，成因却不离奇：山林校区扩建时曾临时开辟多条平行土路，后被植被覆盖且未更新地图，路径高度相似导致方向错觉。",
    verdict: "有历史原型",
    blocks: [
      { type: "p", text: "在山林校区就读过的学生，大多听过后山那片林子的传说：据说进去之后会不知不觉绕回原地，有人走了一下午仍看到那棵标志性的歪脖子树。越是校史长的学校，这类说法越像「祖传」的警告。" },
      { type: "h2", text: "真相是地图没画出来的路" },
      { type: "p", text: "我们在求证时调阅了校区的历史平面图。答案藏在扩建年代里：为了施工与防火，当年临时开辟了多条几乎平行的土路，路间无显著标记。施工结束后这些土路被落叶与灌木覆盖，草草废弃，但并未从现实里消失——它们与正式步道高度相似，交织成一片极易产生「方向错觉」的网格。" },
      { type: "quote", text: "一位参与当年测绘的退休教师说：「当年我们画图时自己都容易绕晕，后来干脆没把那些临时路画上去。」地图上的空白，成了传说滋生的沃土。" },
      { type: "h2", text: "心理学的「再入回路」" },
      { type: "p", text: "当环境缺乏显著地标时，人会产生一种现象：在相似路径中反复走回记忆点，主观上以为「走了很远却回到原地」，实际只是在同一片高度相似的区域打转。这解释了「同一棵歪脖子树」为何反复出现——它可能只是几棵相似树中的代表。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "迷路体验真实存在，但来源是历史遗留的平行土路与缺失的标记系统，属于有历史原型。如今校区已规范步道并增设指示牌，后山传说正慢慢从「警告」变成「纪念」。" },
    ],
  },
  {
    id: "lingyi-jianpan",
    title: "深夜机房的键盘声：是谁在没人时还在打字",
    category: "真相求证",
    tagline: "自助打印机与一台「有人使用」的电脑",
    meta: "求证 · 公共机房",
    place: "某高校公共计算机房",
    time: "二〇一〇年之后",
    readTime: "5 分钟",
    cover: "https://readdy.ai/api/search-image?query=empty%20dark%20computer%20lab%20at%20night%20with%20rows%20of%20sleeping%20monitors%20and%20one%20flickering%20screen%2C%20cool%20and%20warm%20light%20mix%2C%20desaturated%20amber%20and%20slate%2C%20cinematic%20eerie%20editorial&width=1200&height=760&seq=legend-pc-06&orientation=landscape",
    excerpt: "凌晨的机房会传出无人键盘声，甚至有人看到「正在使用中」的电脑。真相指向一台通宵开着的自动更新终端。",
    conclusion: "键盘声来自机房角落一台用于自动巡检与系统更新的终端，它整夜循环执行脚本，远程指令会触发规律键入。并非有人在使用。",
    verdict: "有据可考",
    blocks: [
      { type: "p", text: "高校公共机房在晚间清场后通常无人，但不少值班保安与熬夜学生反映：凌晨能听到角落传来规律的键盘敲击声，甚至有「登录中」「正在使用」状态的电脑空转着屏幕。这则传说在数字化年代成为新一代校园怪谈的代表。" },
      { type: "h2", text: "那台「有人用」的电脑" },
      { type: "p", text: "求证时我们询问了机房管理员。他指向角落里一台不起眼的终端：那是机房长期运行的自动巡检与系统更新机，被设置为整夜循环执行安全脚本。系统会模拟常规键盘输入来完成一些终端命令，因此在安静的环境里听来，就像有人在规律地打字。" },
      { type: "quote", text: "「它的确一晚上都在'打字'，只不过打的是给服务器看的'字'。我们管它叫楼里最勤快的'值夜班'的同学。」" },
      { type: "h2", text: "屏幕为何也会亮" },
      { type: "p", text: "该终端执行夜间更新时会短时点亮屏幕并显示进度，管理员的远程控制也会让光标移动。白天很难注意到，深夜的环境反而放大了这一切，构成了「无人却有人使用」的错觉。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "声音与「有人使用」的现象都有明确的系统来源，且得到机房管理人员的完整解释，属于有据可考。与其说那是校园怪谈，不如说那是信息时代里一台太勤快的电脑。" },
    ],
  },
  {
    id: "xiaoqin-louding",
    title: "旧琴房天花板那首没有弹完的曲子",
    category: "旧事档案",
    tagline: "一段关于音乐系旧事与未竟琴声的记忆",
    meta: "旧事档案 · 旧琴房",
    place: "某艺术院校旧校区",
    time: "上世纪八十年代",
    readTime: "6 分钟",
    cover: "https://readdy.ai/api/search-image?query=old%20dusty%20music%20practice%20room%20with%20an%20aged%20upright%20piano%20in%20dim%20warm%20light%2C%20sheet%20music%20scattered%2C%20worn%20floorboard%2C%20melancholic%20retro%20atmosphere%2C%20amber%20and%20deep%20brown%20editorial&width=1200&height=760&seq=legend-piano-07&orientation=landscape",
    excerpt: "关于旧琴房的故事很多，版本却惊人的一致：都停在同一小节。我们找到了这则记忆可能对应的真实人物。",
    conclusion: "未能在档案中确认与传说完全对应的具体人物，但发现音乐系八十年代确有因伤病中断学业的学生记录，属于证据不足但存在历史环境的存疑档案。",
    verdict: "证据不足存疑",
    blocks: [
      { type: "p", text: "在艺术院校，关于旧琴房天花板的传闻流传很广：据说深夜偶有琴声，且总在某一小节处戛然而止。有趣的是，多个不同版本的故事都停在同一个「没弹完的地方」，这种高度一致性反而耐人寻味。" },
      { type: "h2", text: "一致的细节从何而来" },
      { type: "p", text: "我们在求证时发现，各版本对「停下的小节」描述其实来自早期论坛的一则旧帖，后续传播者多引用该帖。这说明「都停在同一个小节」更多源于文字传播的锚定效应，而非多人独立亲历同一现象。" },
      { type: "h2", text: "是否存在真实原型" },
      { type: "p", text: "旧校区档案中，我们检索到音乐系上世纪八十年代确有因手部伤病而中途中断学业的学生记录，但因年代久远、档案不完整，无法确认其与琴房传闻之间的直接关联。我们将其列为存在历史环境、但证据不足的存疑条目。" },
      { type: "quote", text: "求证原则提醒我们：一处残缺的档案不等于一段确凿的往事。它只能让我们诚实地说一句「曾经有这么一个人，大概率有过」，而不虚构他没有的结局。" },
      { type: "h2", text: "求证结论" },
      { type: "p", text: "综合现有材料，旧琴房传说存在普遍一致的文学叙事，但缺乏可核实的直接亲历者。考虑到档案中存在相似年代与情境的痕迹，我们将其评定为证据不足存疑，并尊重每一位愿意温柔讲述这段集体记忆的人。" },
    ],
  },
];